import { formatCurrency, Summary } from "uikit"
import { ReactComponent as InvestLineIcon } from "uikit/Icons/invest-action.svg"
import React, { useState } from "react"
import { Button, Grid } from "semantic-ui-react"
import { useStore } from "store"
import "./Investments.css"
import { Redirect } from "react-router-dom"
import OutlookIcon from "uikit/Icons/OutlookIcon"
import Methodology from "App/Methodology"

const RISK_PREFERENCES = [
  {
    riskPreference: "conservative",
    additionalLoss: initialAmount => Math.floor(0.0 * initialAmount),
    growthAmount: initialAmount =>
      Math.floor(Math.pow(1.0945, 10) * initialAmount),
    stock: 45,
    bond: 55
  },
  {
    riskPreference: "moderate-conservative",
    additionalLoss: initialAmount => Math.floor(0.05 * initialAmount),
    growthAmount: initialAmount =>
      Math.floor(Math.pow(1.1014, 10) * initialAmount),
    stock: 62,
    bond: 38
  },
  {
    riskPreference: "moderate",
    additionalLoss: initialAmount => Math.floor(0.1 * initialAmount),
    growthAmount: initialAmount =>
      Math.floor(Math.pow(1.1075, 10) * initialAmount),
    stock: 79,
    bond: 21
  },
  {
    riskPreference: "moderate-aggressive",
    additionalLoss: initialAmount => Math.floor(0.15 * initialAmount),
    growthAmount: initialAmount =>
      Math.floor(Math.pow(1.1104, 10) * initialAmount),
    stock: 88,
    bond: 12
  },
  {
    riskPreference: "aggressive",
    additionalLoss: initialAmount => Math.floor(0.16 * initialAmount),
    growthAmount: initialAmount =>
      Math.floor(Math.pow(1.1117, 10) * initialAmount),
    stock: 92,
    bond: 8
  }
]

class InvestmentView extends React.Component {
  constructor(props) {
    super(props)
    const { accounts, household } = props
    const risk_preference = household.risk_preference || "conservative"
    const riskIndex = RISK_PREFERENCES.findIndex(
      ({ riskPreference }) => riskPreference === risk_preference
    )

    const accountsBalance = accounts
      .filter(({ balance }) => balance > 0)
      .reduce((acl, curr) => acl + curr.balance, 0)
    this.initialAmount = Math.max(10000, accountsBalance)
    this.initialLoss = Math.floor(0.1 * this.initialAmount)
    this.debouncer = null
    this.state = {
      riskIndex,
      showActionPlan: false
    }
  }

  componentDidMount() {
    if (this.props.household.risk_preference == null) {
      this.props.submit({
        risk_preference: RISK_PREFERENCES[this.state.riskIndex].riskPreference
      })
    }
  }

  selectRiskIndex = offset => {
    const selectedRiskIndex = this.state.riskIndex + offset
    const { riskPreference: risk_preference } = RISK_PREFERENCES[
      selectedRiskIndex
    ]
    if (this.debouncer !== null) {
      clearTimeout(this.debouncer)
    }
    this.debouncer = setTimeout(
      () => this.props.submit({ risk_preference }),
      500
    )
    this.setState({
      riskIndex: selectedRiskIndex
    })
  }
  render = () => {
    const { household, updateHouseholdState, updateHousehold } = this.props
    if (this.state.showActionPlan) {
      return <Redirect to={this.props.onContinue} />
    }
    const { additionalLoss, bond, growthAmount, stock } = RISK_PREFERENCES[
      this.state.riskIndex
    ]
    return (
      <Summary>
        <Summary.Body>
          <Grid
            centered
            stackable
            style={{
              maxWidth: "800px",
              marginLeft: "auto",
              marginRight: "auto"
            }}
          >
            <Grid.Row style={{ paddingTop: "3em" }}>
              <Grid.Column textAlign="center">
                <h1
                  style={{
                    fontWeight: "300",
                    marginBottom: "0",
                    color: "#4a4a4a"
                  }}
                >
                  Imagine your investments lost{" "}
                  <span style={{ fontWeight: "bold", color: "#d1091b" }}>
                    {formatCurrency(this.initialLoss)}
                  </span>
                  .
                </h1>
                <h2 style={{ fontWeight: "600", marginTop: "0" }}>
                  How much MORE are you willing to LOSE in pursuit
                  <br />
                  of long-term gains?
                </h2>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row
              columns="equal"
              style={{ paddingTop: "3em", paddingBottom: "3em" }}
              centered
            >
              <Grid.Column width={10}>
                <Grid>
                  <Grid.Row columns="equal">
                    <Grid.Column
                      verticalAlign="middle"
                      textAlign="right"
                      width={3}
                      style={{ paddingRight: "0" }}
                    >
                      <Button
                        data-testid="decreaseRisk"
                        disabled={this.state.riskIndex === 0}
                        circular
                        icon="minus"
                        primary
                        onClick={() => this.selectRiskIndex(-1)}
                      />
                    </Grid.Column>
                    <Grid.Column
                      verticalAlign="middle"
                      textAlign="center"
                      style={{ paddingRight: "0", paddingLeft: "10px" }}
                    >
                      <p className="Investment_Amount">
                        {formatCurrency(additionalLoss(this.initialAmount))}
                      </p>
                    </Grid.Column>
                    <Grid.Column
                      verticalAlign="middle"
                      textAlign="left"
                      width={3}
                      style={{ paddingLeft: "10px" }}
                    >
                      <Button
                        data-testid="increaseRisk"
                        disabled={
                          this.state.riskIndex === RISK_PREFERENCES.length - 1
                        }
                        circular
                        icon="plus"
                        primary
                        onClick={() => this.selectRiskIndex(1)}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row style={{ paddingBottom: "2em" }}>
              <Grid.Column textAlign="center">
                <p style={{ fontSize: "1.2em", color: "#4a4a4a" }}>
                  A <strong>{formatCurrency(this.initialAmount)}</strong>{" "}
                  investment portfolio invested in{" "}
                  <strong>{stock}% stocks</strong> and{" "}
                  <strong>{bond}% bonds</strong> has historically grown to{" "}
                  <span
                    style={{
                      fontWeight: "bold",
                      color: "rgb(126, 211, 33)"
                    }}
                  >
                    {formatCurrency(growthAmount(this.initialAmount))}
                  </span>{" "}
                  with ten years of disciplined investing after sustaining an
                  initial loss of{" "}
                  <span style={{ fontWeight: "bold", color: "#d1091b" }}>
                    {formatCurrency(
                      this.initialLoss + additionalLoss(this.initialAmount)
                    )}
                  </span>{" "}
                  (a{" "}
                  {Math.round(
                    (100.0 *
                      (this.initialLoss + additionalLoss(this.initialAmount))) /
                      this.initialAmount
                  )}
                  % loss).
                </p>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <p style={{ color: "#aaa" }}>
                  Investment performance illustrations are averages calculated
                  using historical data starting from 1926 as detailed in the
                  SAVVI{" "}
                  <span className="navlink">
                    <Methodology LinkTitle="methodology" />
                  </span>
                  .
                </p>
                <p style={{ color: "#aaa", fontStyle: "italic" }}>
                  Investments are not guaranteed and are subject to investment
                  risk including the possible loss of principal. The investment
                  return and principal value of the security will fluctuate so
                  that when redeemed, may be worth more or less than the
                  original investment. Past performance is not a guarantee of
                  future results.
                </p>
                <p style={{ color: "#aaa", fontStyle: "italic" }}>
                  Please review our{" "}
                  <span className="navlink">
                    <Methodology LinkTitle="methodology" />
                  </span>{" "}
                  to learn more about the data we use and our assumptions.
                </p>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Summary.Body>
        <Summary.Footer
          nextStep="Create Action Plan"
          style={{ paddingLeft: "0" }}
        >
          <Button
            content="Continue"
            data-testid="continue"
            primary
            onClick={() =>
              this.setState(
                {
                  showActionPlan: true
                },
                async () => {
                  if (!household.has_confirmed_goals) {
                    await updateHousehold({
                      id: household.id,
                      has_confirmed_goals: true
                    })
                  }
                  await updateHouseholdState({
                    id: household.id,
                    isEditingPlan: false
                  })
                }
              )
            }
          />
        </Summary.Footer>
      </Summary>
    )
  }
}
export const Investments = props => {
  const [isInvestmentRiskView, toggleIsInvestmentRiskView] = useState(false)
  const {
    household,
    actions: { updateHouseholdState }
  } = props
  const [accounts, { updateHousehold }] = useStore(
    "householdAccounts",
    household.id
  )

  if (household.risk_preference !== null || isInvestmentRiskView) {
    return (
      <InvestmentView
        household={household}
        accounts={Object.values(accounts)}
        submit={({ risk_preference }) => {
          window.trackEvent("set-risk-preference", { risk_preference })
          updateHousehold({
            id: household.id,
            risk_preference
          })
        }}
        onContinue="/plan/proposal"
        updateHouseholdState={updateHouseholdState}
        updateHousehold={updateHousehold}
      />
    )
  } else {
    return (
      <Summary>
        <Summary.Body>
          <Grid
            container
            stackable
            style={{ paddingTop: "2em", paddingBottom: "2em" }}
          >
            <Grid.Row>
              <Grid.Column textAlign="center">
                <InvestLineIcon
                  style={{ color: "#79ba42", maxWidth: "104px" }}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row centered>
              <Grid.Column width={12}>
                <p className="InvestmentIntroduction__Description">
                  Although past performance doesn't predict the future, savings
                  that haven't been invested have historically lost purchasing
                  power over time as the cost of the things you buy have tended
                  to increase. Just remember how much your first tank of gas
                  cost compared to today. Investing your money offers not only
                  the potential to counteract the eroding effect of inflation
                  but potentially to grow your savings to generate additional
                  wealth.
                </p>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <OutlookIcon style={{ color: "#00adef", maxWidth: "128px" }} />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row centered>
              <Grid.Column width={12}>
                <p className="InvestmentIntroduction__Description">
                  We'll help you align your investment risks with your goals by
                  proposing an investment allocation that matches the money you
                  save to when you spend it. In investment jargon, we help you
                  manage investment risk by "earmarking" your savings and
                  "horizon matching" your investments to the timing of your
                  goals. Long-term savings will have a higher stock allocation
                  while short-term savings will have a higher bond and cash
                  allocation.
                </p>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <svg viewBox="0 0 66 44" style={{ maxWidth: "128px" }}>
                  <g
                    fill="none"
                    stroke="#00adef"
                    strokeWidth="3"
                    strokeMiterlimit="10"
                    strokeDasharray="4,2"
                  >
                    <path d="M59.8,19.6c-7.4-7.9-15.7-11-24.7-11.2c-10.9-0.3-23,6.7-28.6,11.7" />
                    <path d="M52.4,27.5c-2.1-6.8-6.2-19.1-15.8-19.1" />
                  </g>
                  <g fill="#00adef" stroke="none">
                    <polygon points="56.2,21.1 63.5,23.7 61.2,15.6 	" />
                    <polygon points="48,26.9 53.9,31.5 54.9,24.1 	" />
                  </g>
                  <g
                    fill="nont"
                    stroke="black"
                    opacity="0.5"
                    strokeWidth="4"
                    strokeMiterlimit="10"
                  >
                    <path d="M9.5,23v11 M16,34V19 M22.5,16v18 M29,34V12 M35.5,12v22" />
                  </g>
                </svg>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row centered>
              <Grid.Column width={12}>
                <p className="InvestmentIntroduction__Description">
                  To be able to do this, we will next ask you how willing you
                  are to accept various degrees of investment risk in pursuit of
                  long-term investment gains. We'll use your answer to adapt our
                  goal-driven investment recommendations to your risk-taking
                  personality.
                </p>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Summary.Body>
        <Summary.Footer
          nextStep="Choose Risk Preference"
          submitLabel="Continue"
          data-testid="continue"
          continueUrl="/plan/investments/risk-preference"
          onSubmit={() => toggleIsInvestmentRiskView(true)}
        />
      </Summary>
    )
  }
}
